import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, ExclamationCircleIcon, XMarkIcon, ChevronDoubleLeftIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { FaUserFriends, FaClock, FaCalendarAlt, FaUtensils, FaRegClock, FaFire, FaArrowRight, FaRedo, FaUserCog } from 'react-icons/fa';
import { CalendarIcon } from '@heroicons/react/24/outline';
import Topbar from "../global/TopBar";
import Header from "./components/Header";
import DayView from "./components/DayView";
import WeekView from "./components/WeekView";
import MonthView from "./components/MonthView";
import CalendarShareRequestsDialog from "./components/CalendarShareRequestsDialog";
import MealCalendarToggle from "../meal-planner/components/MealCalendarToggle";
import MealWeekView from "./components/MealWeekView";
import { 
  setSelectedDate, 
  setLoadingShared, 
  setSharedEvents, 
  setSharedEventsError,
  respondToCalendarShareRequest
} from '../../state/calendarSlice';
import { fetchMealPlans, generateMealPlan, regenerateMeal } from '../../state/mealPlannerSlice';
import { motion, AnimatePresence } from 'framer-motion';
import calendarService from '../../services/calendarService';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { parseISO } from 'date-fns';
import NewEventForm from './components/NewEventForm';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

const viewComponents = {
  day: DayView,
  week: WeekView,
  month: MonthView,
};

// Define a flag to disable notification features - must match the one in other files
const NOTIFICATIONS_ENABLED = false; // Set to false to disable notifications

// Add a helper function for formatting ingredients at the top before the components
const formatIngredient = (ingredient) => {
  if (typeof ingredient === 'string') {
    return ingredient;
  }
  
  if (typeof ingredient === 'object' && ingredient !== null) {
    const { quantity, unit, name, category } = ingredient;
    let formatted = '';
    
    if (quantity) formatted += `${quantity} `;
    if (unit) formatted += `${unit} `;
    if (name) formatted += name;
    else formatted += 'Ingredient';
    
    return formatted.trim();
  }
  
  return 'Ingredient';
};

// Recipe Modal Component with improved error handling
const RecipeModal = ({ isOpen, onClose, recipe }) => {
  if (!isOpen || !recipe) return null;

  // Safely check if the recipe has valid ingredients array
  const hasValidIngredients = Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0;
  
  // Safely check if the recipe has valid instructions
  const hasValidInstructions = 
    (Array.isArray(recipe.instructions) && recipe.instructions.length > 0) ||
    (typeof recipe.instructions === 'string' && recipe.instructions.trim() !== '');

  // Content to be rendered in the portal
  const modalContent = (
    <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="relative bg-gradient-to-br from-pri-dark/95 to-sec-dark/95 rounded-xl border border-soft-pink/20 shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-soft-pink/10 hover:bg-soft-pink/20 transition-colors z-10"
        >
          <XMarkIcon className="h-6 w-6 text-soft-pink" />
        </button>
        
        {/* Recipe Content */}
        <div className="p-6">
          {/* Recipe Image */}
          {recipe.image && (
            <div className="relative h-56 rounded-lg overflow-hidden mb-6 border border-soft-pink/20">
              <img 
                src={recipe.image} 
                alt={recipe.name || 'Recipe'} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/800x400?text=No+Image';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pri-dark/90 via-transparent to-transparent" />
            </div>
          )}
          
          {/* Recipe Header */}
          <h2 className="text-2xl font-bold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent">
            {recipe.name || 'Recipe Details'}
          </h2>
          
          {/* Recipe Description */}
          <p className="text-pri-gray mt-2 mb-6">
            {recipe.description || 'No description available.'}
          </p>
          
          {/* Recipe Meta */}
          <div className="flex flex-wrap gap-3 mb-6">
            {recipe.calories && (
              <span className="px-3 py-1 rounded-full bg-soft-pink/10 text-soft-pink text-sm">
                {recipe.calories} calories
              </span>
            )}
            {recipe.cookingTime && (
              <span className="px-3 py-1 rounded-full bg-mint-green/10 text-mint-green text-sm">
                {recipe.cookingTime} min cooking time
              </span>
            )}
            {recipe.servings && (
              <span className="px-3 py-1 rounded-full bg-pri-yellow/10 text-pri-yellow text-sm">
                {recipe.servings} servings
              </span>
            )}
          </div>
          
          {/* Ingredients */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-soft-pink mb-3">Ingredients</h3>
            {hasValidIngredients ? (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {recipe.ingredients.map((ingredient, index) => {
                  return (
                    <li key={index} className="flex items-start">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-soft-pink mt-1.5 mr-2 flex-shrink-0"></span>
                      <span className="text-pri-white">
                        {typeof ingredient === 'string' 
                          ? ingredient 
                          : typeof ingredient === 'object' && ingredient !== null
                            ? <>
                                {ingredient.quantity && <span>{ingredient.quantity} </span>}
                                {ingredient.unit && <span>{ingredient.unit} </span>}
                                {ingredient.name || 'Ingredient'}
                                {ingredient.category && <span className="text-xs text-pri-gray ml-1">({ingredient.category})</span>}
                              </>
                            : 'Ingredient'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-pri-gray">No ingredients listed</p>
            )}
          </div>
          
          {/* Instructions */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-soft-pink mb-3">Instructions</h3>
            {hasValidInstructions ? (
              Array.isArray(recipe.instructions) ? (
                <ol className="space-y-4">
                  {recipe.instructions.map((step, index) => (
                    <li key={index} className="flex">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium flex items-center justify-center mr-3">
                        {index + 1}
                      </span>
                      <span className="text-pri-white">{step}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-pri-white">{recipe.instructions}</p>
              )
            ) : (
              <p className="text-pri-gray">No cooking instructions available.</p>
            )}
          </div>
          
          {/* Nutritional Information */}
          {recipe.nutritionalInfo && typeof recipe.nutritionalInfo === 'object' && Object.keys(recipe.nutritionalInfo).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-soft-pink mb-3">Nutritional Information</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {Object.entries(recipe.nutritionalInfo).map(([key, value]) => (
                  <div key={key} className="bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 rounded-lg border border-soft-pink/10 p-3">
                    <span className="block text-sm text-pri-gray">{key}</span>
                    <span className="block text-lg font-medium text-pri-white">
                      {typeof value === 'object' ? JSON.stringify(value) : value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );

  // Use createPortal to render the modal at the end of the document body
  return createPortal(modalContent, document.body);
};

// Add the function to get cuisine images
const getCuisineImageUrl = (cuisineType) => {
  if (!cuisineType) return `${process.env.REACT_APP_CDN_URL}/illustrations/cuisine.png`;
  
  // Convert cuisine type to lowercase and replace spaces with hyphens for consistent URL formatting
  const cuisine = cuisineType.toLowerCase().replace(/ /g, '-');
  
  // List of supported cuisine types
  const supportedCuisines = [
    'italian', 'mexican', 'indian', 'chinese', 'japanese', 
    'thai', 'mediterranean', 'french', 'american', 'greek', 
    'middle-eastern', 'korean', 'vietnamese', 'spanish', 'lebanese', 'south-indian'
  ];
  
  // Check if this cuisine is in our supported list
  if (supportedCuisines.includes(cuisine)) {
    return `${process.env.REACT_APP_CDN_URL}/illustrations/${cuisine}.png`;
  } else {
    // Return a generic cuisine image if specific illustration isn't available
    return `${process.env.REACT_APP_CDN_URL}/illustrations/cuisine.png`;
  }
};

// Meal Day View Component
const MealDayView = ({ meals = {}, date, isLoading = false, onGenerateMealPlan }) => {
  const formattedDate = format(date, 'yyyy-MM-dd');
  const mealTypes = ['breakfast', 'lunch', 'dinner'];
  const isEmpty = Object.keys(meals).length === 0;
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [imageLoadStatus, setImageLoadStatus] = useState({});
  const { userPreferences } = useSelector((state) => state.mealPlanner);
  const navigate = useNavigate();
  
  // Determine if preferences need to be set
  const needsPreferences = !userPreferences || 
    !userPreferences.dietaryRestrictions || 
    !userPreferences.healthGoals;
  
  const openRecipeModal = (recipe) => {
    setSelectedRecipe(recipe);
    setIsRecipeModalOpen(true);
  };
  
  const closeRecipeModal = () => {
    setIsRecipeModalOpen(false);
    setTimeout(() => setSelectedRecipe(null), 200); // Clear recipe after animation
  };
  
  const handleGenerateMealPlan = () => {
    if (typeof onGenerateMealPlan === 'function') {
      onGenerateMealPlan(formattedDate);
    }
  };
  
  const handleAddMeal = (mealType) => {
    if (typeof onGenerateMealPlan === 'function') {
      onGenerateMealPlan(formattedDate, mealType);
    }
  };
  
  const handleImageLoad = (mealType) => {
    setImageLoadStatus(prev => ({
      ...prev,
      [mealType]: true
    }));
  };
  
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-pri-dark">
        <div className="text-center">
          <div className="mb-4 relative w-12 h-12 mx-auto">
            <div className="absolute inset-0 rounded-full border-4 border-t-soft-pink border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-t-transparent border-r-mint-green border-b-transparent border-l-transparent animate-spin"></div>
          </div>
          <p className="text-pri-white">Loading meal plans...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full overflow-y-auto p-4 bg-pri-dark">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold text-soft-pink mb-4">
          Meals for {format(date, 'MMMM d, yyyy')}
        </h2>
        
        {isEmpty ? (
          <div className="py-16 text-center bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 backdrop-blur-sm rounded-xl border border-soft-pink/20 shadow-lg">
            <FaUtensils className="h-16 w-16 mx-auto mb-4 text-soft-pink/30" />
            <h3 className="text-xl font-semibold text-pri-white mb-2">No Meals Planned Yet</h3>
            <p className="text-pri-gray max-w-md mx-auto mb-6">
              You don't have any meals planned for this day. Generate meal plans to see them displayed here.
            </p>
            {needsPreferences ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/meal-planner?view=preferences')}
                className="px-4 py-2 bg-gradient-to-r from-soft-pink/20 to-mint-green/20 hover:from-soft-pink/30 hover:to-mint-green/30 text-pri-white rounded-lg font-medium flex items-center justify-center gap-2 mx-auto"
              >
                <FaUserCog className="text-soft-pink" />
                Set Preferences to Generate
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGenerateMealPlan}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium"
              >
                Generate Meal Plan
              </motion.button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile view - Horizontal scrolling */}
            <div className="md:hidden w-full overflow-x-auto pb-4 snap-x scrollbar-hide">
              <div className="flex gap-4 min-w-min">
                {mealTypes.map((mealType) => {
                  const meal = meals[mealType];
                  // Check if meal has valid recipe data
                  const hasValidRecipe = meal && meal.recipe && typeof meal.recipe === 'object';
                  
                  if (!hasValidRecipe) {
                    return (
                      <motion.div
                        key={mealType}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="snap-center rounded-xl shadow-lg overflow-hidden border border-white/5 bg-gradient-to-br from-pri-dark/80 to-sec-dark/80 h-full w-[220px] flex-shrink-0"
                        whileHover={{ y: -4, scale: 1.02, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)" }}
                      >
                        <div className="bg-pri-dark/60 px-3 py-1.5 border-b border-white/10">
                          <h3 className="font-medium capitalize bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">{mealType}</h3>
                        </div>
                        <div className="p-3 text-center text-pri-white/40 flex flex-col items-center justify-center py-10">
                          <div className="w-16 h-16 rounded-full bg-pri-dark/40 flex items-center justify-center mb-3">
                            <FaUtensils className="w-8 h-8 text-white/20" />
                          </div>
                          <p className="mb-3 text-sm">No meal planned</p>
                          
                          <motion.button
                            onClick={() => handleAddMeal(mealType)}
                            className="mt-2 px-4 py-2 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark text-sm rounded-lg hover:shadow-lg transition-all flex items-center mx-auto font-medium"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <FaUtensils className="mr-2" />
                            Add Meal
                          </motion.button>
                        </div>
                      </motion.div>
                    );
                  }
                  
                  const recipe = meal.recipe;
                  const imageSrc = getCuisineImageUrl(recipe.cuisineType);
                  const isImageLoaded = imageLoadStatus[mealType] || false;
                  const totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0) || recipe.cookingTime || 10;
                  const calories = recipe.nutrients?.calories || recipe.calories || 300;
                  const difficulty = recipe.difficulty || 'Easy';
                  
                  return (
                    <motion.div 
                      key={mealType}
                      className="snap-center rounded-xl shadow-lg overflow-hidden border border-white/10 bg-gradient-to-br from-pri-dark/80 to-sec-dark/80 hover:shadow-xl transition-all relative group flex flex-col w-[220px] flex-shrink-0"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ 
                        y: -4, 
                        scale: 1.02, 
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
                        rotateY: 2
                      }}
                    >
                      {/* Image Container - Square aspect ratio on mobile */}
                      <div className="relative pt-[100%] overflow-hidden bg-pri-dark/40">
                        {/* Background blurred image for loading state */}
                        <div 
                          className="absolute inset-0 bg-cover bg-center blur-sm scale-110"
                          style={{ 
                            backgroundImage: `url(${imageSrc})`,
                            opacity: 0.4
                          }}
                        />
                        
                        {/* Main image */}
                        <img 
                          src={imageSrc}
                          alt={`${recipe.cuisineType || 'Cuisine'} food`}
                          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                          onLoad={() => handleImageLoad(mealType)}
                          onError={() => handleImageLoad(mealType)}
                        />
                        
                        {/* Gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-pri-dark to-transparent opacity-90"></div>
                        
                        {/* Meal type badge */}
                        <div className="absolute top-2 left-2 px-2 py-1 bg-pri-dark/40 rounded-full backdrop-blur-md">
                          <span className="text-xs font-medium text-pri-white capitalize">{mealType}</span>
                        </div>
                      </div>
                      
                      {/* Content - Compact for mobile */}
                      <div className="p-2 flex-grow flex flex-col justify-between">
                        {/* Meal name and description */}
                        <div className="mb-1">
                          <h4 
                            className="font-bold text-sm bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text whitespace-nowrap overflow-hidden text-ellipsis"
                            title={recipe.name} // Show full title on hover
                          >
                            {recipe.name || 'Untitled Recipe'}
                          </h4>
                          <p className="text-xs text-white/80 line-clamp-1 mt-0.5">{recipe.description || 'No description available'}</p>
                        </div>
                        
                        {/* Meal Info */}
                        <div className="grid grid-cols-3 gap-1 mb-2">
                          <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1 px-1">
                            <FaRegClock className="text-soft-pink mb-0.5 text-xs" />
                            <span className="text-[10px] text-pri-white/70">{totalTime} min</span>
                          </div>
                          
                          <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1 px-1">
                            <span className="text-soft-pink text-[10px] font-medium mb-0.5 capitalize">{difficulty}</span>
                            <span className="text-[10px] text-pri-white/70">Level</span>
                          </div>
                          
                          <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1 px-1">
                            <FaFire className="text-orange-400 mb-0.5 text-xs" />
                            <span className="text-[10px] text-pri-white/70">{calories} cal</span>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-1">
                          <motion.button
                            onClick={() => openRecipeModal(recipe)}
                            className="py-1.5 flex-1 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark text-xs rounded-lg hover:opacity-90 transition-colors flex items-center justify-center font-medium"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            View Details
                            <FaArrowRight className="ml-1 text-[10px]" />
                          </motion.button>
                          
                          <motion.button
                            onClick={() => handleAddMeal(mealType)}
                            className="py-1.5 px-1.5 text-pri-white bg-pri-dark/60 hover:text-soft-pink rounded-lg hover:bg-pri-dark/80 transition-colors border border-white/5 flex items-center justify-center"
                            title="Change meal"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            <FaRedo className="w-3 h-3" />
                          </motion.button>
                        </div>
                      </div>
                      
                      {/* Card elevation shadow effect */}
                      <div className="absolute -inset-0.5 bg-gradient-to-b from-soft-pink/5 to-mint-green/5 rounded-xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity blur-sm"></div>
                      
                      {/* Hover effect overlay */}
                      <div 
                        className="absolute inset-0 bg-gradient-to-t from-soft-pink/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      />
                    </motion.div>
                  );
                })}
              </div>
              
              {/* Optional scroll indicator */}
              <div className="mt-3 flex justify-center gap-1">
                {mealTypes.map((_, index) => (
                  <div key={index} className="w-2 h-2 rounded-full bg-soft-pink/30"></div>
                ))}
              </div>
            </div>

            {/* Desktop view - Grid layout */}
            <div className="hidden md:grid md:grid-cols-3 gap-6">
              {mealTypes.map((mealType) => {
                const meal = meals[mealType];
                // Check if meal has valid recipe data
                const hasValidRecipe = meal && meal.recipe && typeof meal.recipe === 'object';
                
                if (!hasValidRecipe) {
                  return (
                    <motion.div
                      key={mealType}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl shadow-lg overflow-hidden border border-white/5 bg-gradient-to-br from-pri-dark/80 to-sec-dark/80 h-full mx-auto w-full max-w-[250px]"
                      whileHover={{ y: -8, scale: 1.02, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)" }}
                    >
                      <div className="bg-pri-dark/60 px-4 py-2 border-b border-white/10">
                        <h3 className="font-medium capitalize bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text">{mealType}</h3>
                      </div>
                      <div className="p-4 text-center text-pri-white/40 flex flex-col items-center justify-center py-16">
                        <div className="w-24 h-24 rounded-full bg-pri-dark/40 flex items-center justify-center mb-3">
                          <FaUtensils className="w-10 h-10 text-white/20" />
                        </div>
                        <p className="mb-3">No meal planned</p>
                        
                        <motion.button
                          onClick={() => handleAddMeal(mealType)}
                          className="mt-2 px-5 py-2.5 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark text-sm rounded-lg hover:shadow-lg transition-all flex items-center mx-auto font-medium"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <FaUtensils className="mr-2" />
                          Add Meal
                        </motion.button>
                      </div>
                    </motion.div>
                  );
                }
                
                const recipe = meal.recipe;
                const imageSrc = getCuisineImageUrl(recipe.cuisineType);
                const isImageLoaded = imageLoadStatus[mealType] || false;
                const totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0) || recipe.cookingTime || 10;
                const calories = recipe.nutrients?.calories || recipe.calories || 300;
                const difficulty = recipe.difficulty || 'Easy';
                
                return (
                  <motion.div 
                    key={mealType}
                    className="rounded-xl shadow-lg overflow-hidden border border-white/10 bg-gradient-to-br from-pri-dark/80 to-sec-dark/80 hover:shadow-xl transition-all relative group h-full flex flex-col mx-auto w-full max-w-[250px]"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ 
                      y: -8, 
                      scale: 1.03, 
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
                      rotateY: 2
                    }}
                  >
                    {/* Image Container - Taller on desktop */}
                    <div className="relative pt-[130%] overflow-hidden bg-pri-dark/40">
                      {/* Background blurred image for loading state */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center blur-sm scale-110"
                        style={{ 
                          backgroundImage: `url(${imageSrc})`,
                          opacity: 0.4
                        }}
                      />
                      
                      {/* Main image */}
                      <img 
                        src={imageSrc}
                        alt={`${recipe.cuisineType || 'Cuisine'} food`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => handleImageLoad(mealType)}
                        onError={() => handleImageLoad(mealType)}
                      />
                      
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-pri-dark to-transparent opacity-90"></div>
                      
                      {/* Meal type badge */}
                      <div className="absolute top-3 left-3 px-3 py-1 bg-pri-dark/40 rounded-full backdrop-blur-md">
                        <span className="text-xs font-medium text-pri-white capitalize">{mealType}</span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-3 flex-grow flex flex-col justify-between">
                      {/* Meal name and description */}
                      <div className="mb-2">
                        <h4 
                          className="font-bold text-base bg-gradient-to-r from-soft-pink to-mint-green text-transparent bg-clip-text whitespace-nowrap overflow-hidden text-ellipsis"
                          title={recipe.name} // Show full title on hover
                        >
                          {recipe.name || 'Untitled Recipe'}
                        </h4>
                        <p className="text-xs text-white/80 line-clamp-1 mt-0.5">{recipe.description || 'No description available'}</p>
                      </div>
                      
                      {/* Meal Info */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1.5 px-1">
                          <FaRegClock className="text-soft-pink mb-0.5 text-sm" />
                          <span className="text-xs text-pri-white/70">{totalTime} min</span>
                        </div>
                        
                        <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1.5 px-1">
                          <span className="text-soft-pink text-xs font-medium mb-0.5 capitalize">{difficulty}</span>
                          <span className="text-xs text-pri-white/70">Level</span>
                        </div>
                        
                        <div className="flex flex-col items-center bg-pri-dark/40 rounded-lg py-1.5 px-1">
                          <FaFire className="text-orange-400 mb-0.5 text-sm" />
                          <span className="text-xs text-pri-white/70">{calories} cal</span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <motion.button
                          onClick={() => openRecipeModal(recipe)}
                          className="py-2 flex-1 bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark text-sm rounded-lg hover:opacity-90 transition-colors flex items-center justify-center font-medium"
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          View Details
                          <FaArrowRight className="ml-1.5 text-xs" />
                        </motion.button>
                        
                        <motion.button
                          onClick={() => handleAddMeal(mealType)}
                          className="py-2 px-2 text-pri-white bg-pri-dark/60 hover:text-soft-pink rounded-lg hover:bg-pri-dark/80 transition-colors border border-white/5 flex items-center justify-center"
                          title="Change meal"
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <FaRedo className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                    
                    {/* Card elevation shadow effect */}
                    <div className="absolute -inset-0.5 bg-gradient-to-b from-soft-pink/5 to-mint-green/5 rounded-xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity blur-sm"></div>
                    
                    {/* Hover effect overlay */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-t from-soft-pink/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    />
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>
      
      {/* Recipe Modal - Now rendered through portal */}
      <AnimatePresence>
        {isRecipeModalOpen && (
          <RecipeModal 
            isOpen={isRecipeModalOpen}
            onClose={closeRecipeModal}
            recipe={selectedRecipe}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Calendar({ onNotificationAction }) {
  const [currentView, setCurrentView] = useState('day');
  const [events, setEvents] = useState([]);
  const [isShareRequestsOpen, setIsShareRequestsOpen] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [isCheckingApi, setIsCheckingApi] = useState(false);
  const [showSharedEvents, setShowSharedEvents] = useState(true);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState('events'); // 'events' or 'meals'
  const selectedDate = useSelector((state) => new Date(state.calendar.selectedDate));
  const user = useSelector((state) => state.auth.user);
  const sharedEvents = useSelector((state) => state.calendar.sharedEvents || []);
  const mealPlans = useSelector((state) => state.mealPlanner.mealPlans || {});
  const mealPlanStatus = useSelector((state) => state.mealPlanner.status);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userPreferences, status } = useSelector((state) => state.mealPlanner);
  const [isNewEventFormOpen, setIsNewEventFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEventsExpanded, setIsEventsExpanded] = useState(false);
  
  // Calculate current month string for dependency tracking
  const currentMonthStr = useMemo(() => format(selectedDate, 'yyyy-MM'), [selectedDate]);
  
  // Add this ref to track the last fetched date range for meal plans
  const lastFetchedMealPlanRange = useRef({ userId: null, startDate: null, endDate: null });
  
  // Effect to fetch events only when user ID or selected month changes
  useEffect(() => {
    if (!user?.id) return;
    
    const fetchEvents = async () => {
      setIsLoading(true);
      try {
        // Use calendarService to fetch events
        const response = await calendarService.fetchMonthEvents(currentMonthStr, user.id);
        const eventsArray = response.events || [];
        
        setEvents(eventsArray);
      } catch (error) {
        console.error('Failed to load events:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEvents();
  }, [user?.id, currentMonthStr]);
  
  // Effect to fetch shared events only when user changes
  useEffect(() => {
    if (!user?.id) return;
    
    const fetchSharedEvents = async () => {
      try {
        dispatch(setLoadingShared(true));
        
        const response = await calendarService.fetchSharedEvents(user.id);
        const sharedEventsData = response.events || [];
        
        dispatch(setSharedEvents(sharedEventsData));
      } catch (error) {
        dispatch(setSharedEventsError('Failed to load shared events'));
      } finally {
        dispatch(setLoadingShared(false));
      }
    };
    
    fetchSharedEvents();
  }, [user?.id, dispatch]);
  
  // Get combined events for upcoming events section
  const upcomingEvents = useMemo(() => {
    if (!user?.id) return [];
    
    // Create a Map to track events by ID and prevent duplicates
    const eventMap = new Map();
    
    // Add user's own events to the map
    events.forEach(event => {
      if (event.id) {
        eventMap.set(event.id, event);
      }
    });
    
    // Add shared events if they should be shown, but only if not already in the map
    if (showSharedEvents && sharedEvents.length > 0) {
      sharedEvents.forEach(event => {
        if (event.id && !eventMap.has(event.id)) {
          eventMap.set(event.id, {...event, isShared: true});
        }
      });
    }
    
    // Convert map values to array and filter/sort
    return Array.from(eventMap.values())
      .filter(event => new Date(event.startTime || event.start) >= new Date())
      .sort((a, b) => new Date(a.startTime || a.start) - new Date(b.startTime || b.start));
  }, [events, sharedEvents, showSharedEvents, user?.id]);
  
  // Get share requests from Redux state
  const shareRequests = useSelector(state => state.calendar.shareRequests || []);
  
  // Limited events for initial display
  const limitedEvents = useMemo(() => {
    return upcomingEvents.slice(0, 3);
  }, [upcomingEvents]);
  
  // Events to display based on expanded state
  const displayedEvents = useMemo(() => {
    return isEventsExpanded ? upcomingEvents : limitedEvents;
  }, [isEventsExpanded, upcomingEvents, limitedEvents]);
  
  // Check API health on component mount and when user changes
  const checkApiHealth = useCallback(async () => {
    if (!user?.id) return;
    
    setIsCheckingApi(true);
    try {
      const isAvailable = await calendarService.checkHealth();
      setApiAvailable(isAvailable);
    } catch (error) {
      setApiAvailable(false);
    } finally {
      setIsCheckingApi(false);
    }
  }, [user?.id]);
  
  useEffect(() => {
    checkApiHealth();
  }, [checkApiHealth]);

  // Check URL parameters to determine if invitation dialog should be shown
  useEffect(() => {
    if (!NOTIFICATIONS_ENABLED) return;
    
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('openInvitations') === 'true') {
      setIsShareRequestsOpen(true);
      
      // Clean up URL after processing
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleCalendarOpen = () => {
    // Always open the mobile sidebar
    setShowMobileSidebar(true);
  };

  const handleDateSelect = (date) => {
    dispatch(setSelectedDate({ selectedDate: date.toString() }));
    setShowMobileSidebar(false);
  };

  const handleEventAdded = (newEvent) => {
    // Add the new event to the events array
    if (newEvent && newEvent.id) {
      // Only add if it's not already in the array
      setEvents(prevEvents => {
        if (!prevEvents.some(event => event.id === newEvent.id)) {
          return [...prevEvents, newEvent];
        }
        return prevEvents;
      });
    }
  };
  
  // Handlers for share requests
  const handleAcceptShare = async (requestId, userId) => {
    try {
      await dispatch(respondToCalendarShareRequest({ requestId, action: 'accept', userId }));
    } catch (error) {
      // Handle error silently
    }
  };

  const handleRejectShare = async (requestId, userId) => {
    try {
      await dispatch(respondToCalendarShareRequest({ requestId, action: 'reject', userId }));
    } catch (error) {
      // Handle error silently
    }
  };

  // Handle previous month navigation
  const handlePreviousMonth = () => {
    dispatch(setSelectedDate({ 
      selectedDate: subMonths(selectedDate, 1).toString() 
    }));
  };
  
  // Handle next month navigation
  const handleNextMonth = () => {
    dispatch(setSelectedDate({ 
      selectedDate: addMonths(selectedDate, 1).toString() 
    }));
  };

  // Effect to fetch meal plans when in meals view
  useEffect(() => {
    if (calendarViewMode === 'meals' && user?.id) {
      const monthStart = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(selectedDate), 'yyyy-MM-dd');
      
      // Check if we already fetched this exact date range for this user
      const lastFetch = lastFetchedMealPlanRange.current;
      const alreadyFetched = lastFetch.userId === user.id && 
                            lastFetch.startDate === monthStart && 
                            lastFetch.endDate === monthEnd &&
                            mealPlanStatus !== 'loading';
      
      // Also check if we already have meal data in the store for this month
      const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
      let hasExistingData = false;
      
      // Check if we have at least some data for this month
      for (let day = 1; day <= daysInMonth; day++) {
        const checkDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
        const formattedDate = format(checkDate, 'yyyy-MM-dd');
        if (mealPlans[formattedDate] && Object.keys(mealPlans[formattedDate]).length > 0) {
          hasExistingData = true;
          break;
        }
      }
      
      if (!alreadyFetched && !hasExistingData) {
        // Update our reference to current fetch parameters
        lastFetchedMealPlanRange.current = {
          userId: user.id,
          startDate: monthStart,
          endDate: monthEnd
        };
        
        // Only dispatch if we don't already have data
        console.log(`Fetching meal plans for ${monthStart} to ${monthEnd}`);
        dispatch(fetchMealPlans({ 
          userId: user.id, 
          startDate: monthStart, 
          endDate: monthEnd 
        }));
      } else {
        console.log('Using existing meal plan data');
      }
    }
  }, [calendarViewMode, selectedDate, user?.id, dispatch, mealPlanStatus, mealPlans]);
  
  // Handle toggle between events and meals view
  const handleViewToggle = (mode) => {
    setCalendarViewMode(mode);
  };
  
  // Get meals for the selected date
  const mealsForSelectedDate = useMemo(() => {
    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    return mealPlans[formattedDate] || {};
  }, [mealPlans, selectedDate]);

  const ViewComponent = viewComponents[currentView];

  // Determine if preferences need to be set
  const needsPreferences = !userPreferences || 
    !userPreferences.dietaryRestrictions || 
    !userPreferences.healthGoals;

  // Handle generating a new meal plan
  const handleGenerateMealPlan = (date, specificMealType) => {
    if (!user?.id) return;
    
    // Check if preferences are set
    if (needsPreferences) {
      // Show a modal or notification about setting preferences
      const shouldSetPreferences = window.confirm(
        "You need to set your dietary preferences and health goals before generating meal plans. Would you like to set them now?"
      );
      
      if (shouldSetPreferences) {
        // Navigate to preferences page
        navigate('/meal-planner?view=preferences');
        return;
      }
      return;
    }
    
    // Parse start and end dates - for simplicity, we just use the same date
    const startDate = date || format(selectedDate, 'yyyy-MM-dd');
    const endDate = date || format(selectedDate, 'yyyy-MM-dd');
    
    if (specificMealType) {
      console.log(`Generating ${specificMealType} for ${startDate}`);
      
      // For regenerating a specific meal type, use regenerateMeal
      dispatch(regenerateMeal({
        userId: user.id,
        date: startDate,
        mealType: specificMealType,
        notes: '' // Can be enhanced later to allow for meal preferences
      }));
    } else {
      console.log(`Generating full meal plan for ${startDate}`);
      
      // Dispatch the generate meal plan action
      dispatch(generateMealPlan({
        userId: user.id,
        startDate,
        endDate,
        notes: '' // Can be enhanced later to allow for meal preferences
      }));
    }
  };

  // Handler for navigating to specific date from week view
  const handleDateClick = (date) => {
    dispatch(setSelectedDate({ selectedDate: date.toString() }));
    setCurrentView('day');
  };

  // Render API unavailable message
  const renderApiUnavailableMessage = () => {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="bg-sec-dark p-6 rounded-lg shadow-xl max-w-md text-center">
          <ExclamationCircleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-pri-white mb-2">Calendar Service Unavailable</h2>
          <p className="text-pri-gray mb-6">
            We're having trouble connecting to the calendar service. Please try again later or contact support if the problem persists.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={checkApiHealth}
            disabled={isCheckingApi}
            className="flex items-center justify-center w-full bg-pri-yellow text-pri-dark py-2 rounded-md font-semibold"
          >
            {isCheckingApi ? (
              <>
                <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Try Again
              </>
            )}
          </motion.button>
        </div>
      </div>
    );
  };

  // Mobile Calendar Popup - with improved layout and close button position
  const renderCalendarPopup = () => {
    if (!showMobileSidebar) return null;
    
    const daysInMonth = eachDayOfInterval({
      start: startOfMonth(selectedDate),
      end: endOfMonth(selectedDate),
    });

    return (
      <AnimatePresence>
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
          className="fixed inset-y-0 right-0 w-full max-w-xs z-50"
        >
          <motion.div 
            className="h-full bg-gradient-to-b from-pri-dark/95 to-sec-dark/95 backdrop-blur-xl shadow-xl border-l border-soft-pink/10 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {/* Glossy Effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/5 via-transparent to-mint-green/5" />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
              <div className="absolute right-0 top-0 -mr-24 -mt-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-20" />
              <div className="absolute right-0 bottom-0 -mr-24 -mb-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
              <div className="absolute left-0 top-0 w-px h-full bg-gradient-to-b from-transparent via-soft-pink/20 to-transparent" />
            </div>
            
            {/* Sidebar Header with Title and Close Button */}
            <div className="relative p-4 border-b border-soft-pink/10 flex justify-between items-center">
              <h2 className="text-lg font-semibold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent">
                Calendar
              </h2>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowMobileSidebar(false)}
                className="p-2 rounded-full bg-soft-pink/10 hover:bg-soft-pink/20 transition-colors duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-soft-pink" />
              </motion.button>
            </div>
            
            <div className="relative p-5 space-y-6">
              {/* Today Button */}
              <motion.button
                whileHover={{ scale: 1.03, boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)' }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  dispatch(setSelectedDate({ selectedDate: new Date().toString() }));
                  setShowMobileSidebar(false);
                }}
                className="w-full py-2.5 px-4 bg-gradient-to-r from-soft-pink/20 to-mint-green/10 backdrop-blur-sm rounded-xl text-pri-white font-medium border border-soft-pink/20 shadow-lg transition-all duration-300"
              >
                Today: {format(new Date(), 'MMMM d, yyyy')}
              </motion.button>
              
              {/* Mini Calendar */}
              <div className="relative overflow-hidden rounded-xl border border-soft-pink/20 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 backdrop-blur-sm shadow-lg">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handlePreviousMonth}
                      className="p-1 rounded-lg text-pri-white bg-gradient-to-br from-soft-pink/10 to-mint-green/5 hover:from-soft-pink/20 hover:to-mint-green/10 transition-all duration-300"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </motion.button>
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent">
                      {format(selectedDate, 'MMMM yyyy')}
                    </h3>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleNextMonth}
                      className="p-1 rounded-lg text-pri-white bg-gradient-to-br from-soft-pink/10 to-mint-green/5 hover:from-soft-pink/20 hover:to-mint-green/10 transition-all duration-300"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </motion.button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-center mb-2">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                      <div key={`day-${i}`} className="text-xs text-soft-pink/70 font-medium">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {/* Add empty placeholder cells for days before the first of the month */}
                    {Array.from({ length: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).getDay() }).map((_, i) => (
                      <div key={`empty-${i}`} className="w-8 h-8" />
                    ))}
                    
                    {/* Render the actual days of the month */}
                    {Array.from({ length: new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate() }).map(
                      (_, i) => {
                        const day = i + 1;
                        const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                        const isSelectedDay = date.getDate() === selectedDate.getDate();
                        const isToday = date.toDateString() === new Date().toDateString();
                        
                        return (
                          <motion.button
                            key={`date-${day}`}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDateSelect(date)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-200 ${
                              isSelectedDay
                                ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium'
                                : isToday
                                ? 'bg-soft-pink/20 text-soft-pink font-medium'
                                : 'text-pri-gray hover:bg-soft-pink/10 hover:text-soft-pink'
                            }`}
                          >
                            {day}
                          </motion.button>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>
              
              {/* Shared Events Toggle */}
              <div className="relative p-4 rounded-xl border border-soft-pink/20 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 backdrop-blur-sm shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaUserFriends className="text-soft-pink mr-2" />
                    <span className="text-pri-white font-medium">Shared Events</span>
                  </div>
                  <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                    <input
                      type="checkbox"
                      id="shared-events-toggle-mobile"
                      className="absolute w-6 h-6 opacity-0 z-10 cursor-pointer border border-pri-white"
                      checked={showSharedEvents}
                      onChange={(e) => setShowSharedEvents(e.target.checked)}
                    />
                    <label
                      htmlFor="shared-events-toggle-mobile"
                      className={`block h-6 border  border-pri-white overflow-hidden rounded-full cursor-pointer transition-colors duration-300 ${
                        showSharedEvents ? 'bg-gradient-to-r from-soft-pink to-mint-green' : 'bg-gray-700'
                      }`}
                    >
                      <span
                        className={`absolute block w-6 h-6 rounded-full border border-pri-white ${
                          showSharedEvents ? 'bg-pri-dark' : 'bg-pri-white'
                        }  shadow inset-y-0 transition-transform duration-300 ${
                          showSharedEvents ? 'right-0' : 'left-0'
                        }`}
                      />
                    </label>
                  </div>
                </div>
              </div>
              
              {/* Upcoming Events */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-soft-pink">Upcoming Events</h3>
                <div className="p-4 rounded-xl border border-soft-pink/20 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 backdrop-blur-sm shadow-lg">
                  {displayedEvents.length > 0 ? (
                    <div className="space-y-3">
                      <AnimatePresence>
                        {displayedEvents.map((event, index) => (
                          <motion.div
                            key={event.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="p-3 rounded-lg bg-soft-pink/10 hover:bg-soft-pink/20 transition-colors duration-200 cursor-pointer"
                            onClick={() => {
                              // Navigate to the day view for this event's date
                              const eventDate = new Date(event.startTime || event.start);
                              dispatch(setSelectedDate({ selectedDate: eventDate.toString() }));
                              setCurrentView('day');
                              // Close mobile sidebar if open
                              setShowMobileSidebar(false);
                            }}
                          >
                            <h4 className="font-medium text-pri-white">{event.title}</h4>
                            <div className="flex items-center text-xs text-pri-gray mt-1">
                              <FaCalendarAlt className="mr-1 h-3 w-3 text-soft-pink" />
                              <span className="font-medium text-soft-pink">
                                {format(parseISO(event.startTime || event.start), 'EEE, MMM d')}
                              </span>
                            </div>
                            <div className="flex items-center text-xs text-pri-gray mt-1">
                              <FaClock className="mr-1 h-3 w-3" />
                              <span>
                                {format(parseISO(event.startTime || event.start), 'h:mm a')}
                              </span>
                            </div>
                            {event.location && (
                              <div className="flex items-center text-xs text-pri-gray mt-1">
                                <FaMapMarkerAlt className="mr-1 h-3 w-3" />
                                <span>{event.location}</span>
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                      
                      {upcomingEvents.length > 3 && (
                        <div className="text-center">
                          <motion.button 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="text-soft-pink text-sm hover:text-soft-pink/80 transition-colors flex items-center justify-center mx-auto"
                            onClick={() => setIsEventsExpanded(!isEventsExpanded)}
                          >
                            {isEventsExpanded ? (
                              <>
                                <ChevronUpIcon className="h-4 w-4 mr-1" />
                                Show less
                              </>
                            ) : (
                              <>
                                <ChevronDownIcon className="h-4 w-4 mr-1" />
                                Show more ({upcomingEvents.length})
                              </>
                            )}
                          </motion.button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-pri-gray">
                      <p>No upcoming events</p>
                      <button 
                        onClick={() => {
                          setIsNewEventFormOpen(true);
                          setShowMobileSidebar(false);
                        }}
                        className="mt-2 py-1 px-3 text-sm bg-soft-pink/20 text-soft-pink rounded-md hover:bg-soft-pink/30 transition-colors duration-200"
                      >
                        + Create Event
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
        
        {/* Backdrop overlay */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black z-40"
          onClick={() => setShowMobileSidebar(false)}
        />
      </AnimatePresence>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-pri-dark">
      <Topbar />
      <Header
        currentView={currentView}
        setCurrentView={setCurrentView}
        onCalendarOpen={handleCalendarOpen}
        onEventAdded={handleEventAdded}
        onOpenShareRequests={() => setIsShareRequestsOpen(true)}
      >
        {/* Add MealCalendarToggle with proper alignment for Header */}
        <MealCalendarToggle 
          view={calendarViewMode}
          onChange={handleViewToggle}
      />
      </Header>
      
      {/* Add mobile calendar button container here, outside of the flex container */}
      <div className="md:hidden relative h-0 z-30">
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute right-4 top-3 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-soft-pink to-mint-green shadow-lg shadow-soft-pink/20"
          onClick={() => setShowMobileSidebar(!showMobileSidebar)}
          whileHover={{ scale: 1.1, boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)' }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronDoubleLeftIcon className="h-5 w-5 text-pri-dark" />
        </motion.button>
      </div>
      
      <div className="flex-1 flex overflow-hidden">
        {apiAvailable ? (
          <>
            {/* Main Calendar View - Conditional Rendering based on calendarViewMode */}
            <div className="flex-1 overflow-hidden">
              <AnimatePresence mode="wait">
                {calendarViewMode === 'events' ? (
                  <motion.div
                    key="events-view"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="h-full"
                  >
              <ViewComponent showSharedEvents={showSharedEvents} />
                  </motion.div>
                ) : currentView === 'day' ? (
                  <motion.div
                    key="meals-view-day"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="h-full"
                  >
                    <MealDayView
                      meals={mealsForSelectedDate}
                      date={selectedDate}
                      isLoading={mealPlanStatus === 'loading'}
                      onGenerateMealPlan={handleGenerateMealPlan}
                    />
                  </motion.div>
                ) : currentView === 'week' ? (
                  <motion.div
                    key="meals-view-week"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="h-full"
                  >
                    <MealWeekView
                      mealPlans={mealPlans}
                      selectedDate={selectedDate}
                      onDateClick={handleDateClick}
                      isLoading={mealPlanStatus === 'loading'}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="meals-view-month"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="h-full"
                  >
                    <div className="h-full flex flex-col items-center justify-center bg-pri-dark p-6">
                      <FaUtensils className="h-16 w-16 text-soft-pink/30 mb-4" />
                      <h3 className="text-xl font-semibold text-pri-white mb-2">Meal View Available in Day and Week Views</h3>
                      <p className="text-pri-gray max-w-md text-center mb-6">
                        Detailed meal plans are currently only available in day and week views. Please switch to one of these views to see your meal plans.
                      </p>
                      <div className="flex gap-4">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setCurrentView('day')}
                          className="px-4 py-2 rounded-lg bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium"
                        >
                          Switch to Day View
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setCurrentView('week')}
                          className="px-4 py-2 rounded-lg bg-gradient-to-r from-soft-pink/70 to-mint-green/70 text-pri-dark font-medium"
                        >
                          Switch to Week View
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Desktop Sidebar Panel - Enhanced with soft-pink accents */}
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring", damping: 25 }}
              className="hidden md:block w-80 border-l border-soft-pink/10 h-full relative"
            >
              <div className="h-full bg-gradient-to-b from-pri-dark/80 to-sec-dark/80 backdrop-blur-xl overflow-y-auto">
                {/* Glossy Effects */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/5 via-transparent to-mint-green/5" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
                  <div className="absolute right-0 top-0 -mr-24 -mt-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-20" />
                  <div className="absolute right-0 bottom-0 -mr-24 -mb-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
                  <div className="absolute left-0 top-0 w-px h-full bg-gradient-to-b from-transparent via-soft-pink/20 to-transparent" />
                </div>
                
                <div className="relative p-5 space-y-6">
                  {/* Today Button */}
                  <motion.button
                    whileHover={{ scale: 1.03, boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => dispatch(setSelectedDate({ selectedDate: new Date().toString() }))}
                    className="w-full py-2.5 px-4 bg-gradient-to-r from-soft-pink/20 to-mint-green/10 backdrop-blur-sm rounded-xl text-pri-white font-medium border border-soft-pink/20 shadow-lg transition-all duration-300"
                  >
                    Today: {format(new Date(), 'MMMM d, yyyy')}
                  </motion.button>
                  
                  {/* Mini Calendar */}
                  <div className="relative overflow-hidden rounded-xl border border-soft-pink/20 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 backdrop-blur-sm shadow-lg">
                    <div className="p-4">
                      <div className="flex justify-between items-center mb-4">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={handlePreviousMonth}
                          className="p-1 rounded-lg text-pri-white bg-gradient-to-br from-soft-pink/10 to-mint-green/5 hover:from-soft-pink/20 hover:to-mint-green/10 transition-all duration-300"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </motion.button>
                        <h3 className="text-lg font-semibold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent">
                          {format(selectedDate, 'MMMM yyyy')}
                        </h3>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={handleNextMonth}
                          className="p-1 rounded-lg text-pri-white bg-gradient-to-br from-soft-pink/10 to-mint-green/5 hover:from-soft-pink/20 hover:to-mint-green/10 transition-all duration-300"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </motion.button>
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1 text-center mb-2">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                          <div key={`day-${i}`} className="text-xs text-soft-pink/70 font-medium">
                            {day}
                          </div>
                        ))}
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1">
                        {/* Add empty placeholder cells for days before the first of the month */}
                        {Array.from({ length: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).getDay() }).map((_, i) => (
                          <div key={`empty-${i}`} className="w-8 h-8" />
                        ))}
                        
                        {/* Render the actual days of the month */}
                        {Array.from({ length: new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate() }).map(
                          (_, i) => {
                            const day = i + 1;
                            const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                            const isSelectedDay = date.getDate() === selectedDate.getDate();
                            const isToday = date.toDateString() === new Date().toDateString();
                            
                            return (
                              <motion.button
                                key={`date-${day}`}
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleDateSelect(date)}
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-200 ${
                                  isSelectedDay
                                    ? 'bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium'
                                    : isToday
                                    ? 'bg-soft-pink/20 text-soft-pink font-medium'
                                    : 'text-pri-white hover:bg-soft-pink/10'
                                }`}
                              >
                                {day}
                              </motion.button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Shared Events Toggle */}
                  <div className="flex items-center justify-between p-3 rounded-xl border border-soft-pink/20 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 backdrop-blur-sm shadow-lg">
                    <div className="flex items-center gap-2">
                      <FaUserFriends className="h-4 w-4 text-soft-pink" />
                      <span className="text-sm font-medium text-pri-white">Shared Events</span>
                    </div>
                    <div 
                      onClick={() => setShowSharedEvents(!showSharedEvents)}
                      className={`relative w-12 h-6 transition-colors duration-300 border border-pri-white rounded-full cursor-pointer ${
                        showSharedEvents ? 'bg-soft-pink' : 'bg-pri-dark/60'
                      }`}
                    >
                      <div 
                        className={`absolute top-1 h-4 w-4  rounded-full ${
                          showSharedEvents ? 'bg-pri-dark' : 'bg-pri-white'
                        }  shadow-md transform transition-transform duration-300 ${
                          showSharedEvents ? 'translate-x-7' : 'translate-x-1' 
                        }`} 
                      />
                    </div>
                  </div>
                  
                  {/* Upcoming Events Section */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent">
                      Upcoming Events
                    </h3>
                    
                    {displayedEvents.length > 0 ? (
                      <div className="space-y-2">
                        <AnimatePresence>
                          {displayedEvents.map((event, index) => (
                            <motion.div
                              key={event.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -20 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                              whileHover={{ scale: 1.02, x: 3 }}
                              className="p-3 rounded-xl border border-soft-pink/20 bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 backdrop-blur-sm shadow-lg cursor-pointer"
                              onClick={() => {
                                // Navigate to the day view for this event's date
                                const eventDate = new Date(event.startTime || event.start);
                                dispatch(setSelectedDate({ selectedDate: eventDate.toString() }));
                                setCurrentView('day');
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-2 h-full rounded-full" style={{ backgroundColor: event.color || '#3B82F6' }} />
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-pri-white truncate">{event.title}</h4>
                                  <div className="text-xs text-soft-pink font-medium mt-1 flex items-center">
                                    <FaCalendarAlt className="mr-1 h-2.5 w-2.5" />
                                    <span>{format(parseISO(event.startTime || event.start), 'EEE, MMM d')}</span>
                                  </div>
                                  <div className="text-xs text-pri-gray mt-1 flex items-center">
                                    <FaClock className="mr-1 h-2.5 w-2.5" />
                                    <span>
                                      {format(parseISO(event.startTime || event.start), 'h:mm a')} - 
                                      {format(parseISO(event.endTime || event.end), 'h:mm a')}
                                    </span>
                                  </div>
                                  {event.location && (
                                    <div className="text-xs text-pri-gray mt-1 flex items-center">
                                      <FaMapMarkerAlt className="mr-1 h-2.5 w-2.5" />
                                      <span className="truncate">{event.location}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        
                        {upcomingEvents.length > 3 && (
                          <div className="text-center">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="text-soft-pink text-sm hover:text-soft-pink/80 transition-colors flex items-center justify-center mx-auto"
                              onClick={() => setIsEventsExpanded(!isEventsExpanded)}
                            >
                              {isEventsExpanded ? (
                                <>
                                  <ChevronUpIcon className="h-4 w-4 mr-1" />
                                  Show less
                                </>
                              ) : (
                                <>
                                  <ChevronDownIcon className="h-4 w-4 mr-1" />
                                  Show more ({upcomingEvents.length})
                                </>
                              )}
                            </motion.button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-4 rounded-xl border border-soft-pink/10 bg-gradient-to-br from-pri-dark/40 to-sec-dark/40 backdrop-blur-sm text-center">
                        <p className="text-pri-gray text-sm">No upcoming events</p>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setIsNewEventFormOpen(true)}
                          className="mt-2 px-3 py-1 text-xs rounded-lg bg-gradient-to-r from-soft-pink/20 to-mint-green/20 text-soft-pink"
                        >
                          + Create Event
                        </motion.button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        ) : (
          renderApiUnavailableMessage()
        )}
      </div>
      
      {/* New Event Form */}
      <NewEventForm
        isOpen={isNewEventFormOpen}
        onClose={() => setIsNewEventFormOpen(false)}
        onEventAdded={handleEventAdded}
      />
      
      {/* Calendar Share Requests Dialog */}
      <CalendarShareRequestsDialog
        isOpen={isShareRequestsOpen}
        onClose={() => setIsShareRequestsOpen(false)}
        onAccept={handleAcceptShare}
        onReject={handleRejectShare}
        shareRequests={shareRequests}
      />
      
      {/* Mobile Calendar Popup */}
      {renderCalendarPopup()}
    </div>
  );
}
